import React from 'react';
import { Globe, Search, Share2, Bell, User, MoreHorizontal, TrendingUp } from 'lucide-react';
import { SectionHeader } from './SectionHeader';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

// Views by Source Component
// Note: This shows estimated breakdown based on typical Telegram channel patterns
// Actual API data for views breakdown requires Telegram Statistics API (channel must have 500+ subscribers)
export function ViewsBySource({ channelStats, postsData }) {
  // Calculate estimated sources based on post patterns
  // This is an approximation - real data would come from Telegram Statistics API
  const totalViews = postsData?.reduce((sum, post) => sum + (post.views || 0), 0) || 0;
  const totalForwards = postsData?.reduce((sum, post) => sum + (post.forwards || 0), 0) || 0;
  
  // Estimate view sources based on channel behavior patterns
  const forwardViews = Math.round(totalForwards * 3.5); // Each forward generates ~3.5 views
  const directViews = Math.round(totalViews * 0.45); // ~45% direct channel visits
  const notificationViews = Math.round(totalViews * 0.25); // ~25% from notifications
  const searchViews = Math.round(totalViews * 0.05); // ~5% from search
  const otherViews = totalViews - forwardViews - directViews - notificationViews - searchViews;
  
  const sourceData = [
    { name: 'Direct', value: Math.max(0, directViews), color: '#3B82F6', icon: Globe, description: 'Opened channel directly' },
    { name: 'Notifications', value: Math.max(0, notificationViews), color: '#F59E0B', icon: Bell, description: 'Clicked from notification' },
    { name: 'Forwards', value: Math.max(0, forwardViews), color: '#10B981', icon: Share2, description: 'From forwarded posts' },
    { name: 'Search', value: Math.max(0, searchViews), color: '#8B5CF6', icon: Search, description: 'Found via search' },
    { name: 'Other', value: Math.max(0, otherViews), color: '#6B7280', icon: MoreHorizontal, description: 'Profile links, etc.' },
  ].filter(d => d.value > 0);
  
  const hasData = totalViews > 0;
  
  return (
    <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
      <SectionHeader 
        icon={Globe} 
        title="Views by Source" 
        subtitle="Where your views come from (estimated)"
      />
      
      <div className="p-6">
        {hasData ? (
          <div className="grid grid-cols-2 gap-6">
            {/* Pie Chart */}
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie
                    data={sourceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {sourceData.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1F2937', 
                      border: '1px solid #374151', 
                      borderRadius: '8px',
                      color: '#F9FAFB',
                      padding: '8px 12px'
                    }}
                    formatter={(value, name) => {
                      const percent = totalViews > 0 ? ((value / totalViews) * 100).toFixed(1) : 0;
                      return [`${value.toLocaleString()} (${percent}%)`, name];
                    }}
                  />
                </RechartsPie>
              </ResponsiveContainer>
            </div>
            
            {/* Source Breakdown */}
            <div className="space-y-3">
              {sourceData.map((source, idx) => {
                const Icon = source.icon;
                const percent = totalViews > 0 ? ((source.value / totalViews) * 100) : 0;
                
                return (
                  <div 
                    key={idx}
                    className="p-3 rounded-lg border transition-all hover:shadow-md"
                    style={{ backgroundColor: `${source.color}10`, borderColor: `${source.color}30` }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: `${source.color}20` }}>
                          <Icon size={12} style={{ color: source.color }} />
                        </div>
                        <span className="text-sm font-medium text-white">{source.name}</span>
                      </div>
                      <span className="text-sm font-mono font-bold" style={{ color: source.color }}>
                        {percent.toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-[#6B7280]">{source.description}</span>
                      <span className="text-xs text-[#9CA3AF]">{source.value.toLocaleString()} views</span>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-2 h-1 bg-[hsl(var(--border))] rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all"
                        style={{ width: `${percent}%`, backgroundColor: source.color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-[#6B7280]">
            <Globe size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No view data available</p>
            <p className="text-xs mt-1">Start posting to see source breakdown</p>
          </div>
        )}
        
        {/* Info Note */}
        {hasData && (
          <div className="mt-4 p-3 bg-[#F59E0B]/10 border border-[#F59E0B]/20 rounded-lg">
            <div className="flex items-start gap-2">
              <TrendingUp size={14} className="text-[#F59E0B] mt-0.5 shrink-0" />
              <p className="text-[10px] text-[#F59E0B]">
                <strong>Note:</strong> This is an estimated breakdown based on typical Telegram channel patterns. 
                For exact data, Telegram Statistics API requires 500+ subscribers.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ViewsBySource;
